import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../auth/AuthContext';

const CompanyContext = createContext();

export function useCompany() {
  return useContext(CompanyContext);
}

export function CompanyProvider({ children }) {
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser, userProfile, updateUserProfile } = useAuth();

  // Fetch company info when user profile changes
  useEffect(() => {
    async function fetchCompanyInfo() {
      // Reset state when user profile changes
      setCompanyInfo(null);
      setError(null);
      
      if (!userProfile) {
        setLoading(false);
        return;
      }
      
      if (!userProfile.company) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const companyRef = doc(db, "companies", userProfile.company);
        const companySnap = await getDoc(companyRef);
        
        if (companySnap.exists()) {
          setCompanyInfo(companySnap.data());
        } else {
          console.warn("Company document doesn't exist:", userProfile.company);
          setCompanyInfo(null);
        }
      } catch (error) {
        console.error("Error fetching company info:", error);
        setError(error.message);
        setCompanyInfo(null);
      } finally {
        setLoading(false);
      }
    }

    fetchCompanyInfo();
  }, [userProfile]);

  // Update company info in Firestore
  async function updateCompanyInfo(data) {
    if (!currentUser) return false;
    
    try {
      // If the company document already exists, update it
      if (userProfile && userProfile.company) {
        const companyRef = doc(db, "companies", userProfile.company);
        
        // Add updated timestamp
        const updatedData = {
          ...data,
          updatedAt: new Date()
        };
        
        // Update the company document in Firestore
        await updateDoc(companyRef, updatedData);
        
        // Update local state
        setCompanyInfo(prev => ({ ...prev, ...updatedData }));
        return true;
      } 
      // If no company is linked to the user yet, create a new company document
      else {
        // Create a new company ID if not provided
        const companyId = data.id || `company_${currentUser.uid}_${Date.now()}`;
        const companyRef = doc(db, "companies", companyId);
        
        // Add created timestamp and user ID
        const newCompanyData = {
          ...data,
          id: companyId,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: currentUser.uid
        };
        
        // Save the new company document to Firestore
        await setDoc(companyRef, newCompanyData);
        
        // Update user profile with company reference
        await updateUserProfile({
          company: companyId,
          isConfigured: true
        });
        
        // Update local state
        setCompanyInfo(newCompanyData);
        return true;
      }
    } catch (error) {
      console.error("Error updating company info:", error);
      setError(error.message);
      return false;
    }
  }

  const value = {
    companyInfo,
    updateCompanyInfo,
    loading,
    error
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export default CompanyContext;
